import { createInitialState, WEEKDAYS, normalizeDayTimeboxes } from '../data/seed'
import { safeId } from './safeId'

const GOAL_KEYS = [
  'professionalYearly',
  'privateYearly',
  'professionalMonthly',
  'privateMonthly',
  'professionalWeekly',
  'privateWeekly',
]

/** Merge imported data onto initial state so we always have valid shape and timeboxTypes. */
function mergeIntoInitialState(partial) {
  const state = createInitialState()
  if (partial?.goals && typeof partial.goals === 'object') {
    GOAL_KEYS.forEach(k => {
      if (partial.goals[k] != null) state.goals[k] = String(partial.goals[k]).trim()
    })
  }
  if (partial?.days && typeof partial.days === 'object') {
    WEEKDAYS.forEach(day => {
      const d = partial.days[day]
      if (!d || !Array.isArray(d.timeboxes)) return
      const timeboxes = d.timeboxes.map(t => ({
        id: safeId(),
        typeId: String(t.typeId ?? '').trim() || undefined,
        startSlot: Number(t.startSlot),
        lengthSlots: Number(t.lengthSlots) || 1,
      })).filter(t => t.typeId)
      state.days[day] = {
        timeboxes: normalizeDayTimeboxes(timeboxes),
        locked: !!d.locked,
        dayGoal: String(d.dayGoal ?? '').trim(),
      }
    })
  }
  return state
}

/**
 * Parse JSON export into app state. Expects { goals?, days? }.
 */
export function parseJSON(text) {
  let data
  try {
    data = JSON.parse(text)
  } catch {
    throw new Error('Invalid JSON')
  }
  return mergeIntoInitialState(data)
}

/**
 * Parse CSV: row 0 = goal keys, row 1 = goal values; row 3 = header "day,typeId,startSlot,lengthSlots"; rest = timebox rows.
 */
export function parseCSV(text) {
  const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean)
  const state = createInitialState()

  if (lines.length >= 2) {
    const keys = parseCSVLine(lines[0])
    const values = parseCSVLine(lines[1])
    keys.forEach((k, i) => {
      if (GOAL_KEYS.includes(k) && values[i] != null) state.goals[k] = String(values[i]).trim()
    })
  }

  const dayCol = 0
  const typeIdCol = 1
  const startSlotCol = 2
  const lengthSlotsCol = 3
  let i = 2
  while (i < lines.length) {
    const row = parseCSVLine(lines[i])
    if (row[dayCol]?.toLowerCase() === 'day' && row[typeIdCol]?.toLowerCase() === 'typeid') {
      i++
      break
    }
    i++
  }
  while (i < lines.length) {
    const row = parseCSVLine(lines[i])
    const day = row[dayCol]
    const typeId = row[typeIdCol]?.trim()
    const startSlot = Number(row[startSlotCol])
    const lengthSlots = Number(row[lengthSlotsCol]) || 1
    if (WEEKDAYS.includes(day) && typeId && !Number.isNaN(startSlot)) {
      const existing = state.days[day]?.timeboxes ?? []
      state.days[day] = {
        ...state.days[day],
        timeboxes: normalizeDayTimeboxes([
          ...existing,
          { id: safeId(), typeId, startSlot, lengthSlots },
        ]),
        locked: state.days[day]?.locked ?? false,
        dayGoal: state.days[day]?.dayGoal ?? '',
      }
    }
    i++
  }

  return state
}

function parseCSVLine(line) {
  const out = []
  let cur = ''
  let inQuotes = false
  for (let i = 0; i < line.length; i++) {
    const c = line[i]
    if (c === '"') {
      inQuotes = !inQuotes
    } else if (inQuotes) {
      cur += c
    } else if (c === ',' || c === ';') {
      out.push(cur.trim())
      cur = ''
    } else {
      cur += c
    }
  }
  out.push(cur.trim())
  return out
}

/**
 * Load a file (JSON or CSV) and return app state.
 */
export function loadFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const text = e.target?.result
        if (typeof text !== 'string') {
          reject(new Error('File could not be read as text'))
          return
        }
        const name = (file.name || '').toLowerCase()
        const state = name.endsWith('.json') ? parseJSON(text) : parseCSV(text)
        resolve(state)
      } catch (err) {
        reject(err)
      }
    }
    reader.onerror = () => reject(new Error('Failed to read file'))
    reader.readAsText(file)
  })
}

/**
 * Export state as JSON string.
 */
export function exportJSON(state) {
  return JSON.stringify(
    {
      goals: state.goals ?? {},
      days: state.days ?? {},
    },
    null,
    2
  )
}

/**
 * Export state as CSV string.
 */
export function exportCSV(state) {
  const rows = []
  rows.push(GOAL_KEYS.join(','))
  rows.push(GOAL_KEYS.map(k => escapeCSV((state.goals ?? {})[k] ?? '')).join(','))
  rows.push('')
  rows.push('day,typeId,startSlot,lengthSlots')
  WEEKDAYS.forEach(day => {
    const dayData = state.days?.[day]
    const list = dayData?.timeboxes ?? []
    list.forEach(t => {
      rows.push([day, t.typeId, t.startSlot ?? 1, t.lengthSlots ?? 1].map(escapeCSV).join(','))
    })
  })
  return rows.join('\n')
}

function escapeCSV(val) {
  const s = String(val ?? '')
  if (s.includes(',') || s.includes('"') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`
  return s
}

/**
 * Trigger download of a string as a file.
 */
export function downloadFile(content, filename, mimeType = 'application/octet-stream') {
  const blob = new Blob([content], { type: mimeType })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}
